import { styled } from '@/stitches.config';
import { Box } from '@base-app';

import { ReactComponent as ArrowIcon } from '@assets/icons/simple-arrow.svg';

const commonStyles = {
  '.react-calendar & button': {
    fontFamily: 'inherit',
  },

  '.react-calendar__navigation__arrow': {
    borderColor: '$darkBlue100',
  },

  '.react-calendar__navigation button:enabled:hover, .react-calendar__navigation button:enabled:focus': {
    backgroundColor: 'transparent',
  },
};

export const StyledRangeCalendarContainer = styled(Box, {
  ...commonStyles,
  marginTop: '$4',
  marginBottom: '$8',
  marginLeft: 'auto',
  marginRight: 'auto',
  fontFamily: 'inherit',

  '.react-calendar': {
    fontFamily: 'inherit',
    border: '0',
    margin: 'auto',
    width: '434px',
  },

  // Navigation
  '.react-calendar__navigation': {
    marginBottom: '$0',
  },
  '.react-calendar__navigation__label': {
    color: '$darkBlue100',
    fontWeight: '$bold',
    fontSize: '$4',
    lineHeight: '$6',
  },

  '.react-calendar__month-view__days': {
    gap: '$2 $0',
  },

  // Weekdays
  '.react-calendar__month-view__weekdays__weekday': {
    color: '$darkBlue400',
    fontWeight: '$bold',
    fontSize: '$normal',
    lineHeight: '$6',
    width: '48px',
    textTransform: 'none',

    '& abbr': {
      textDecoration: 'none',
    },
  },

  // Days
  '.react-calendar__month-view__days__day': {
    fontWeight: '$medium',
    fontSize: '$xs',
    lineHeight: '$6',
    padding: '$1',
    width: '56px',
    height: '$8',
    margin: '0',
  },

  '.react-calendar__tile--now': {
    backgroundColor: '$white',
  },

  '.react-calendar__tile:hover': {
    borderRadius: '$6',
  },

  '.react-calendar__tile:enabled:hover, .react-calendar__tile:enabled:focus': {
    backgroundColor: '$blue300',
    color: '$white',
  },

  '.react-calendar__tile--active,.react-calendar__tile--hover': {
    backgroundColor: '$blue800',
    color: 'inherit',

    '&:disabled': {
      backgroundColor: '#f0f0f0',
      color: 'rgba(16, 16, 16, 0.3)',
    },
  },

  '.react-calendar__month-view__days__day--weekend': {
    color: 'inherit',

    '&:disabled': {
      color: 'rgba(16, 16, 16, 0.3)',
    },
  },

  '.react-calendar__tile--hoverStart,.react-calendar__tile--rangeStart': {
    borderTopLeftRadius: '$6',
    borderBottomLeftRadius: '$6',
  },

  '.react-calendar__tile--hoverEnd,.react-calendar__tile--rangeEnd': {
    borderTopRightRadius: '$6',
    borderBottomRightRadius: '$6',
  },

  '.react-calendar__tile--rangeStart, .react-calendar__tile--rangeEnd': {
    backgroundColor: '$blue300',
  },

  '.react-calendar__tile--rangeBothEnds.react-calendar__tile--rangeStart.react-calendar__tile--rangeEnd': {
    borderRadius: '$0',
  },

  '.react-calendar__tile--hover.react-calendar__tile--hoverEnd': {
    borderRadius: '$0',
  },

  '.react-calendar--selectRange .react-calendar__tile--hover': {
    backgroundColor: 'transparent',

    '&:disabled': {
      backgroundColor: '#f0f0f0',
      color: 'rgba(16, 16, 16, 0.3)',
      cursor: 'not-allowed',
    },
  },

  '.react-calendar__month-view__days__day--neighboringMonth': {
    color: '$darkGray300',
  },
});

export const StyledCalendarContainer = styled(Box, {
  ...commonStyles,
  fontFamily: 'inherit',

  '.react-calendar': {
    backgroundColor: '$coolGray700',
    border: '0',
    borderRadius: '$radii$4',
    fontFamily: 'inherit',
    padding: '32px 64px',
    width: '520px',
  },

  variants: {
    variant: {
      small: {
        '.react-calendar': {
          padding: '16px 32px',
        },
      },
    },
  },

  // Navigation
  '.react-calendar__navigation': {
    marginBottom: '$2',
  },
  '.react-calendar__navigation__label': {
    color: '$charcoal300',
    fontWeight: '$bold',
    fontSize: '$md',
    lineHeight: '$6',
    pointerEvents: 'none',
  },

  '.react-calendar__month-view__days': {
    gap: '$2 $4',
  },

  // Weekdays
  '.react-calendar__month-view__weekdays__weekday': {
    color: '$darkBlue300',
    fontWeight: '$bold',
    fontSize: '$md',
    lineHeight: '$6',
    marginBottom: '$4',
    textTransform: 'none',
    textAlign: 'left',

    '& abbr': {
      textDecoration: 'none',
    },
  },

  // Days
  '.react-calendar__month-view__days__day': {
    color: '$charcoal300',
    fontWeight: '$regular',
    fontSize: '$md',
    lineHeight: '$6',
    margin: '0',
    padding: '$1',
  },

  '.react-calendar__month-view__days__day--weekend': {
    color: '$charcoal300',
  },

  '.react-calendar__tile': {
    height: '$10',
    maxWidth: '$10',
    borderRadius: '$6',
  },

  '.react-calendar__tile--now': {
    backgroundColor: '$white',
    border: '2px solid $blue300',
    color: '$blue300',
    fontWeight: '$bold',
  },

  '.react-calendar__tile--now:disabled': {
    backgroundColor: 'transparent',
    border: '2px solid $charcoal700',
    color: '$charcoal700',
    fontWeight: '$bold',
  },

  '.react-calendar__tile:hover': {
    backgroundColor: '$blue800',
    borderRadius: '$6',
  },

  '.react-calendar__tile:enabled:hover': {
    backgroundColor: '$blue800',
  },

  '.react-calendar__tile:enabled:focus': {
    backgroundColor: '$blue300',
  },

  '.react-calendar__tile--active,.react-calendar__tile--hover': {
    backgroundColor: '$blue300',
    borderRadius: '$6',
    color: '$white',
  },

  '.react-calendar__tile--active:enabled:hover': {
    backgroundColor: '$blue300',
  },

  '.react-calendar__tile:enabled:active': {
    backgroundColor: '$blue600',
    color: '$charcoal300',
  },

  '.react-calendar__tile:disabled': {
    backgroundColor: 'transparent',
    color: '$charcoal700',
    textDecoration: 'line-through',
    pointerEvents: 'none',
  },
});

export const StyledArrowIcon = styled(ArrowIcon, {
  color: '$darkGray300',
  variants: {
    variant: {
      right: {
        rotate: '180deg',
      },
    },
  },
});
